/*
 * Fragment Loader
*/

import Event from '../events';
import EventHandler from '../event-handler';
import {ErrorTypes, ErrorDetails} from '../errors';
import {logger} from '../utils/logger';

class FragmentLoader extends EventHandler {

  constructor(hls) {
    super(hls, Event.FRAG_LOADING);
    this.loaders = {};
    this._lastLevel = -1;
  }

  destroy() {
    let loaders = this.loaders;
    for (let loaderName in loaders) {
      let loader = loaders[loaderName];
      if (loader) {
        loader.destroy();
      }
    }
    this.loaders = {};
    EventHandler.prototype.destroy.call(this);
  }

  onFragLoading(data) {
    let frag = data.frag,
        type = frag.type,
        loader = this.loaders[type],
        config = this.hls.config;

    frag.loaded = 0;
    if (loader) {
      logger.warn(`abort previous fragment loader for type:${type}`);
      loader.abort();
    }
    loader  = this.loaders[type] = frag.loader = typeof(config.fLoader) !== 'undefined' ? new config.fLoader(config) : new config.loader(config);

    let loaderContext, loaderConfig, loaderCallbacks;
    loaderContext = { url : frag.url, frag : frag, responseType : 'arraybuffer', progressData : false};
    let start = frag.byteRangeStartOffset, end = frag.byteRangeEndOffset;
    if (!isNaN(start) && !isNaN(end)) {
      loaderContext.rangeStart = start;
      loaderContext.rangeEnd = end;
    }
    loaderConfig = { timeout : config.fragLoadingTimeOut, maxRetry : 0 , retryDelay : 0, maxRetryDelay : config.fragLoadingMaxRetryTimeout};
    loaderCallbacks = { onSuccess : this.loadsuccess.bind(this), onError :this.loaderror.bind(this), onTimeout : this.loadtimeout.bind(this), onProgress: this.loadprogress.bind(this)};
    loader.load(loaderContext,loaderConfig,loaderCallbacks);
  }

  loadsuccess(response, stats, context, networkDetails=null) {
    let payload = response.data, frag = context.frag;
    // detach fragment loader on load success
    frag.loader = undefined;
    this.loaders[frag.type] = undefined;

    let levels = this.hls.levels,
        level = levels[frag.level];

    // Factor out the measured transcode latency from the requested start so its not
    // included in the bandwidth measure.
    //
    if (Number.isFinite(stats.transcodeLatency)) {
      // Fudge by increasing trequest by the latency, but not exceeding tfirst
      stats.trequest = Math.min(stats.trequest + stats.transcodeLatency, stats.tfirst);

      level.details.maxTranscodeLatency = Math.max(level.details.maxTranscodeLatency || 0, stats.transcodeLatency);
    }

    let
      levelBitrate = level.realBitrate ? Math.max(level.realBitrate,level.bitrate) : level.bitrate,
      realBitrate = level.realBitrate ? Math.round(level.realBitrate / 1024) : 'unknown',
      bitrateKbps = Math.round(levelBitrate / 1024),
      havePreTranscoded = Number.isFinite(stats.preTranscodedByteCount),
      preTransBytes = havePreTranscoded ? stats.preTranscodedByteCount : 'none';

    // Set rcvdBufferedSegment the first time we receive a segment that the transcoder has pre-transcoded at least
    // some of it.  If the header is not being generated by the source and the config indicates it should be (we
    // specified a value for 'initialBufferedGoal', then just guess based on the number of complete loaded fragments
    // at this level.
    //
    if (havePreTranscoded) {
      level.details.rcvdBufferedSegment = level.details.rcvdBufferedSegment || (havePreTranscoded && preTransBytes > 0);
    } else {
      const requiredConsecutiveSegments = 3;
      let details = level.details;
      let prevNloaded = frag.sn - details.startSN > requiredConsecutiveSegments;

      if (prevNloaded) {
        let prevN = details.fragments.slice(frag.sn - requiredConsecutiveSegments, frag.sn);
        let prevNloaded = true;
        prevN.forEach(fragment => {
          prevNloaded = prevNloaded && fragment.loaded && fragment.loaded > 0;
        });
      }
      level.details.rcvdBufferedSegment = level.details.rcvdBufferedSegment || prevNloaded;
    }
    if (this._lastLevel >= 0 && this._lastLevel !== frag.level) {
      levels[this._lastLevel].details.rcvdBufferedSegment = false;
    }

    this._lastLevel = frag.level;

    logger.info(`fragLoaded.abr(${frag.sn}:${frag.level}): tcode latency: ${stats.transcodeLatency} net latency: ${Math.round(stats.tfirst-stats.trequest)} size (B): ${stats.loaded} buffered (B): ${preTransBytes}  BW (kbps): ${Math.round(8*stats.loaded/(stats.tload-stats.trequest))}  level/observed (kbps): ${bitrateKbps}/${realBitrate}`);

    this.hls.trigger(Event.FRAG_LOADED, {payload: payload, frag: frag, stats: stats, networkDetails: networkDetails});
  }

  loaderror(response, context, networkDetails=null) {
    let loader = context.loader;
    if (loader) {
      loader.abort();
    }
    this.loaders[context.type] = undefined;
    this.hls.trigger(Event.ERROR, {type: ErrorTypes.NETWORK_ERROR, details: ErrorDetails.FRAG_LOAD_ERROR, fatal: false, frag: context.frag, response: response, networkDetails: networkDetails});
  }

  loadtimeout(stats, context, networkDetails=null) {
    let loader = context.loader;
    if (loader) {
      loader.abort();
    }
    this.loaders[context.type] = undefined;
    this.hls.trigger(Event.ERROR, {type: ErrorTypes.NETWORK_ERROR, details: ErrorDetails.FRAG_LOAD_TIMEOUT, fatal: false, frag: context.frag, networkDetails: networkDetails});
  }

  // data will be used for progressive parsing
  loadprogress(stats, context, data, networkDetails=null) { // jshint ignore:line
    let frag = context.frag;
    frag.loaded = stats.loaded;


    let chunkRequestTime = stats.tlastprogress ? stats.tlastprogress : stats.trequest;
    stats.tlastprogress = performance.now();
    stats.chunkDelta = performance.now() - chunkRequestTime;

    this.hls.trigger(Event.FRAG_LOAD_PROGRESS, {frag: frag, stats: stats, networkDetails: networkDetails});
  }
}

export default FragmentLoader;



// WEBPACK FOOTER //
// ./src/loader/fragment-loader.js